/** @format */

import {
	DraftQuestionInput,
	draftQuestionSchema,
	buildPaperId,
	type Brand,
	type CoreRef,
	type PaperQualification,
} from "@question-bank/shared";
import Papa from "papaparse";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Locale } from "../features/i18n/init";
import {
	listQualifications,
	listSeries,
	listSubjects,
	listTiers,
	type Qualification,
	type ReferenceItem,
} from "../api/coreTaxonomy";
import { useBrand } from "../hooks/useBrand";
import {
	validatePaperSelection,
	validateRowSpecification,
	type CoreWarning,
	type PaperLevelSelection,
} from "../features/csvImport/coreValidation";
import { PaperLevelForm, type PaperLevelOption } from "./PaperLevelForm";
import { EMPTY_PAPER_LEVEL, isPaperLevelComplete, type PaperLevelValues } from "./paperLevel";

type CsvValidationProps = {
	csvFile: File;
};

type ValidationError = {
	rowIndex: number;
	field: string;
	message: string;
};

type RowValidationResult = {
	rowIndex: number;
	data: Partial<DraftQuestionInput>;
	isValid: boolean;
	errors: ValidationError[];
	/** Advisory Core API warnings for this row (never block the import). */
	warnings: CoreWarning[];
};

// Question-level required fields only. Paper-level facts (subject, level,
// series, date, tier) now come from the on-page selection, not the CSV (DR8).
const requiredFields = ["Question Number", "Max Marks", "Language Code"];

// CSV columns the import still reads — question-level only. The paper-level
// columns (Brand, Subject, Level, Series, Date, Tier, Assessment code) are
// intentionally dropped: the paper-level selection on the page wins (DR8).
const columnMapping: Record<string, string> = {
	"Language Code": "language",
	"Question Number": "questionNumber",
	"Max Marks": "maxMarks",
	"A01 marks": "ao1",
	"A02 marks": "ao2",
	"A03 marks": "ao3",
	Theme: "theme",
	Topic: "topic",
	"Sub-Topic": "subTopic",
	"Question Type": "questionType",
	"Low Demand Marks": "lowDemandMarks",
	"Medium Demand Marks": "mediumDemandMarks",
	"High Demand Marks": "highDemandMarks",
	"Question Key Word (Explain, describe) ": "keyWord",
	"Optional question": "optional",
	"Common with other tier (series assessment question title)": "commonWithOtherTier",
	"Seeded or Double marked": "seededOrDoubleMarked",
	"Auto marked or Examiner Marked": "markingMethod",
};

const parseValue = (value: string, fieldName: string): string | number | boolean | undefined => {
	if (!value || value.trim() === "-" || value.trim() === "") {
		return undefined;
	}

	const trimmed = value.trim();

	if (fieldName === "language") {
		return trimmed.toLowerCase();
	}

	if (["maxMarks", "ao1", "ao2", "ao3", "lowDemandMarks", "mediumDemandMarks", "highDemandMarks"].includes(fieldName)) {
		const num = Number.parseInt(trimmed, 10);
		return Number.isNaN(num) ? undefined : num;
	}

	if (fieldName === "optional") {
		return trimmed.toUpperCase() === "Y";
	}

	return trimmed;
};

const mapRowToSchema = (csvRow: Record<string, string>, paperId: string, brand: Brand): Partial<DraftQuestionInput> => {
	const mapped: Partial<DraftQuestionInput> = { paperId, brand };

	for (const [csvCol, csvValue] of Object.entries(csvRow)) {
		const schemaField = columnMapping[csvCol];
		if (!schemaField) {
			continue;
		}

		const value = parseValue(csvValue, schemaField);
		if (value === undefined) {
			continue;
		}
		if (schemaField === "ao1" || schemaField === "ao2" || schemaField === "ao3") {
			mapped.assessmentObjectives ??= {};
			mapped.assessmentObjectives[schemaField] = value as number;
		} else if (schemaField === "theme" || schemaField === "topic" || schemaField === "subTopic") {
			mapped.specification ??= {};
			mapped.specification[schemaField] = value as string;
		} else if (
			schemaField === "questionType" ||
			schemaField === "lowDemandMarks" ||
			schemaField === "mediumDemandMarks" ||
			schemaField === "highDemandMarks" ||
			schemaField === "keyWord" ||
			schemaField === "optional" ||
			schemaField === "commonWithOtherTier" ||
			schemaField === "seededOrDoubleMarked" ||
			schemaField === "markingMethod"
		) {
			mapped.metadata ??= {};

			if (schemaField === "lowDemandMarks") {
				mapped.metadata.demandMarks ??= {};
				mapped.metadata.demandMarks.low = value as number;
			} else if (schemaField === "mediumDemandMarks") {
				mapped.metadata.demandMarks ??= {};
				mapped.metadata.demandMarks.medium = value as number;
			} else if (schemaField === "highDemandMarks") {
				mapped.metadata.demandMarks ??= {};
				mapped.metadata.demandMarks.high = value as number;
			} else if (schemaField === "questionType") {
				mapped.metadata.questionType = value as string;
			} else if (schemaField === "keyWord") {
				mapped.metadata.keyWord = value as string;
			} else if (schemaField === "optional") {
				mapped.metadata.optional = value as boolean;
			} else if (schemaField === "commonWithOtherTier") {
				mapped.metadata.commonWithOtherTier = value as string;
			} else if (schemaField === "seededOrDoubleMarked") {
				mapped.metadata.seededOrDoubleMarked = value as string;
			} else if (schemaField === "markingMethod") {
				mapped.metadata.markingMethod = value as "A" | "E";
			}
		} else {
			(mapped as Record<string, unknown>)[schemaField] = value;
		}
	}
	return mapped;
};

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const getErrorSuggestion = (field: string, message: string): string => {
	const firstField = field.split(".")[0];
	const capitalField = capitalize(firstField);
	if (message.includes("Invalid enum value")) {
		return `${capitalField}: Invalid value. Check your file for typos or incorrect values.`;
	}
	if (message.includes("required")) {
		return `${capitalField}: This field is required. Please fill it in and re-upload`;
	}

	return message;
};

/** Build the paper-level selection used for Core validation from the form. */
const toSelection = (brand: Brand, values: PaperLevelValues): PaperLevelSelection => ({
	brand,
	subject: values.subject.trim(),
	// `qualification`, `season` and `tier` hold the Core codes (QB2-223 / DR11).
	qualification: values.qualification,
	tier: values.tier || undefined,
	season: values.season,
	year: Number.parseInt(values.year, 10),
});

export const CsvValidation = ({ csvFile }: CsvValidationProps) => {
	// The brand is fixed per host; the import always targets the current brand.
	const { brand } = useBrand();

	const [paperLevel, setPaperLevel] = useState<PaperLevelValues>(EMPTY_PAPER_LEVEL);
	const [subjectOptions, setSubjectOptions] = useState<string[]>([]);
	// Core-sourced reference data for the paper-level dropdowns (QB2-223 / DR11).
	// Core is a hard dependency for import: when a read fails the list stays
	// empty and the dropdown offers nothing — there is NO enum fallback.
	const [qualifications, setQualifications] = useState<Qualification[]>([]);
	const [tierItems, setTierItems] = useState<ReferenceItem[]>([]);
	const [seriesItems, setSeriesItems] = useState<ReferenceItem[]>([]);
	const [results, setResults] = useState<RowValidationResult[]>([]);
	const [paperWarnings, setPaperWarnings] = useState<CoreWarning[]>([]);
	const [isValidating, setIsValidating] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const navigate = useNavigate();
	const { locale } = useParams<{ locale: Locale }>();

	// Load the Core-sourced reference data once per brand so the qualification,
	// subject, tier and series controls are dropdowns. Core is a hard dependency
	// for import (DR11): a failed read leaves the list empty — no enum fallback.
	// Subject keeps a free-text input when its read fails.
	useEffect(() => {
		let isCancelled = false;
		listSubjects(brand)
			.then((list) => {
				if (!isCancelled) {
					setSubjectOptions(list.data.map((subject) => subject.title.en));
				}
			})
			.catch(() => {
				// Core unavailable — the subject control falls back to free text.
				if (!isCancelled) {
					setSubjectOptions([]);
				}
			});
		listQualifications(brand)
			.then((list) => {
				if (!isCancelled) {
					setQualifications(list.data);
				}
			})
			.catch(() => {
				if (!isCancelled) {
					setQualifications([]);
				}
			});
		listTiers(brand)
			.then((list) => {
				if (!isCancelled) {
					setTierItems(list.data);
				}
			})
			.catch(() => {
				if (!isCancelled) {
					setTierItems([]);
				}
			});
		listSeries(brand)
			.then((list) => {
				if (!isCancelled) {
					setSeriesItems(list.data);
				}
			})
			.catch(() => {
				if (!isCancelled) {
					setSeriesItems([]);
				}
			});
		return () => {
			isCancelled = true;
		};
	}, [brand]);

	// Qualification options: value = Core `code`, label = title (DR11, no enum
	// fallback). Empty when the Core read failed.
	const qualificationOptions = useMemo<PaperLevelOption[]>(
		() => qualifications.map((qualification) => ({ value: qualification.code, label: qualification.title.en })),
		[qualifications],
	);

	// Tier/series options: value = Core `code` (the item id), label = title.
	// Empty when the Core read failed — the dropdown then offers nothing (DR11).
	const tierOptions = useMemo<PaperLevelOption[]>(
		() => tierItems.map((item) => ({ value: item.id, label: item.title.en })),
		[tierItems],
	);

	const seriesOptions = useMemo<PaperLevelOption[]>(
		() => seriesItems.map((item) => ({ value: item.id, label: item.title.en })),
		[seriesItems],
	);

	// Resolve a Core option `code` to its display title from the loaded options,
	// so each Paper CoreRef stores both the code and the resolved title.
	const resolveQualificationTitle = useCallback(
		(code: string): string | undefined => qualificationOptions.find((option) => option.value === code)?.label,
		[qualificationOptions],
	);
	const resolveTierTitle = useCallback(
		(code: string): string | undefined => tierOptions.find((option) => option.value === code)?.label,
		[tierOptions],
	);
	const resolveSeasonTitle = useCallback(
		(code: string): string | undefined => seriesOptions.find((option) => option.value === code)?.label,
		[seriesOptions],
	);

	const isPaperReady = isPaperLevelComplete(paperLevel);

	const validateFile = useCallback(async () => {
		if (!isPaperReady) {
			setResults([]);
			setPaperWarnings([]);
			return;
		}
		try {
			setIsValidating(true);
			setError(null);

			const selection = toSelection(brand, paperLevel);
			const paperId = buildPaperId({
				brand,
				paperCode: paperLevel.paperCode.trim(),
				series: { season: selection.season, year: selection.year },
			});

			// Validate the paper-level selection against Core once per run.
			const paperResult = await validatePaperSelection(selection);
			setPaperWarnings(paperResult.warnings);

			const parsedData = await new Promise<Papa.ParseResult<Record<string, string>>>((resolve, reject) => {
				Papa.parse(csvFile, { header: true, complete: resolve, error: reject });
			});

			if (!parsedData.data || parsedData.data.length === 0) {
				setError("CSV file is empty");
				return;
			}

			const nonEmptyRows = parsedData.data.filter((row) => Object.values(row).some((val) => val && val.trim() !== ""));

			// Validate one row: required-field + schema checks, then the advisory
			// Core specification check (warn, never block). Pure per row except for
			// the Core read, which is cached per normalised subtopic, so rows that
			// share a subtopic do not each hit the network.
			const validateRow = async (csvRow: Record<string, string>, index: number): Promise<RowValidationResult> => {
				const rowNum = index + 2;
				const errors: ValidationError[] = [];

				for (const requiredField of requiredFields) {
					const value = csvRow[requiredField];
					if (!value || value.trim() === "" || value.trim() === "-") {
						errors.push({ rowIndex: rowNum, field: requiredField, message: `${requiredField} is required` });
					}
				}

				const mappedData = mapRowToSchema(csvRow, paperId, brand);

				const result = draftQuestionSchema.safeParse(mappedData);
				if (!result.success) {
					result.error.errors.forEach((zodError) => {
						errors.push({ rowIndex: rowNum, field: zodError.path.join("."), message: zodError.message });
					});
				}

				let warnings: CoreWarning[] = [];
				if (paperResult.currentEdition) {
					const rowResult = await validateRowSpecification(selection, paperResult.currentEdition, {
						theme: mappedData.specification?.theme,
						topic: mappedData.specification?.topic,
						subTopic: mappedData.specification?.subTopic,
					});
					warnings = rowResult.warnings;
					if (rowResult.resolved) {
						mappedData.specification = {
							...mappedData.specification,
							coreLineageId: rowResult.resolved.lineageId,
							coreEditionId: rowResult.resolved.uuid,
						};
					}
				}

				return { rowIndex: rowNum, data: mappedData, isValid: errors.length === 0, errors, warnings };
			};

			// Run rows with bounded concurrency: parallel enough to avoid N serial
			// round-trips, capped so a large paper does not fire thousands of
			// concurrent requests at the Core API (which would exhaust the browser
			// connection pool and risk rate limits). Results stay in row order.
			const MAX_CONCURRENT_ROWS = 10;
			const validationResults: RowValidationResult[] = new Array(nonEmptyRows.length);
			let nextRow = 0;
			const runWorker = async (): Promise<void> => {
				while (nextRow < nonEmptyRows.length) {
					const current = nextRow++;
					validationResults[current] = await validateRow(nonEmptyRows[current], current);
				}
			};
			await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT_ROWS, nonEmptyRows.length) }, runWorker));
			setResults(validationResults);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to validate file");
		} finally {
			setIsValidating(false);
		}
	}, [csvFile, brand, paperLevel, isPaperReady]);

	useEffect(() => {
		validateFile();
	}, [validateFile]);

	const validRows = results?.filter((r) => r.isValid) ?? [];
	const invalidRows = results?.filter((r) => !r.isValid) || [];
	const warningCount = paperWarnings.length + validRows.reduce((sum, row) => sum + row.warnings.length, 0);

	const handleDryRun = () => {
		setIsLoading(true);
		try {
			const selection = toSelection(brand, paperLevel);
			const qualificationTitle = resolveQualificationTitle(selection.qualification);
			const qualification: PaperQualification = {
				code: selection.qualification,
				...(qualificationTitle ? { title: qualificationTitle } : {}),
			};
			// Build the Paper CoreRefs from the selected Core codes, resolving the
			// display titles from the loaded Core lists so the Paper stores both.
			const seasonTitle = resolveSeasonTitle(selection.season);
			const season: CoreRef = {
				code: selection.season,
				...(seasonTitle ? { title: seasonTitle } : {}),
			};
			const tierTitle = selection.tier ? resolveTierTitle(selection.tier) : undefined;
			const tier: CoreRef | undefined = selection.tier
				? { code: selection.tier, ...(tierTitle ? { title: tierTitle } : {}) }
				: undefined;
			const previewData = {
				questions: validRows.map((r) => r.data as DraftQuestionInput),
				totalQuestions: validRows.length,
				paper: {
					paperCode: paperLevel.paperCode.trim(),
					subject: selection.subject,
					qualification,
					...(tier ? { tier } : {}),
					season,
					year: selection.year,
					date: paperLevel.date,
				},
				coreWarnings: {
					paper: paperWarnings,
					// Keyed by position in the `questions` array (which is built from
					// `validRows` in the same order), so the preview table can line each
					// warning up with its row. NOT the CSV line number (`rowIndex`),
					// which has gaps where invalid rows were filtered out.
					rows: validRows
						.map((row, index) => ({ index, warnings: row.warnings }))
						.filter((entry) => entry.warnings.length > 0),
				},
			};
			navigate(`/${locale}/admin/import-preview`, { state: { previewData } });
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to run preview");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div>
			<PaperLevelForm
				values={paperLevel}
				onChange={setPaperLevel}
				subjectOptions={subjectOptions}
				qualificationOptions={qualificationOptions}
				tierOptions={tierOptions}
				seriesOptions={seriesOptions}
				isDisabled={isValidating}
			/>

			{!isPaperReady && (
				<p className="mb-4 text-sm text-slate-500">Complete the paper details above to validate the uploaded file.</p>
			)}

			{isValidating && <div>Validating…</div>}
			{error && <div className="text-red-600">{error}</div>}

			{isPaperReady && !isValidating && !error && warningCount > 0 && (
				<div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3">
					<h3 className="mb-2 font-bold text-amber-800">Core API warnings: {warningCount}</h3>
					<p className="mb-2 text-sm text-amber-700">
						These do not block the import. Review them in the dry-run before importing.
					</p>
					<ul className="list-disc pl-5 text-sm text-amber-700">
						{paperWarnings.map((warning, index) => (
							<li key={`paper-${index}`}>{warning.message}</li>
						))}
					</ul>
				</div>
			)}

			{isPaperReady && !isValidating && invalidRows && invalidRows.length > 0 && (
				<div className="mb-4">
					<h3 className="font-bold mb-2">Invalid Rows: {invalidRows?.length}</h3>
					<div className="overflow-x-auto overflow-y-auto max-h-96 rounded-lg border border-slate-200 bg-white shadow-(--admin-shadow-raised)">
						<table className="min-w-full divide-y divide-slate-200 text-left text-sm">
							<thead className="bg-slate-50 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
								<tr>
									<th scope="col" className="px-3 py-2">
										Row
									</th>
									<th scope="col" className="px-3 py-2">
										Question Number
									</th>
									<th scope="col" className="px-3 py-2">
										Errors
									</th>
								</tr>
							</thead>
							<tbody>
								{invalidRows?.map((row) => (
									<tr key={row.rowIndex} className="transition-colors hover:bg-slate-50">
										<td className="px-4 py-3 text-slate-700">{row.rowIndex}</td>
										<td className="px-4 py-3 text-slate-700">{row.data.questionNumber}</td>
										<td className="px-4 py-3 text-red-600">
											<ul>
												{row.errors.map((err, errorIndex) => (
													<li key={errorIndex}>{getErrorSuggestion(err.field, err.message)}</li>
												))}
											</ul>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{isPaperReady && !isValidating && validRows && validRows.length > 0 && (
				<>
					<div className="mb-4">
						<h3 className="font-bold mb-2">Valid Rows: {validRows?.length}</h3>
						<div className="overflow-x-auto overflow-y-auto max-h-96 rounded-lg border border-slate-200 bg-white shadow-(--admin-shadow-raised)">
							<table className="min-w-full divide-y divide-slate-200 text-left text-sm">
								<thead className="bg-slate-50 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
									<tr>
										<th scope="col" className="px-3 py-2">
											Row
										</th>
										<th scope="col" className="px-3 py-2">
											Question Number
										</th>
										<th scope="col" className="px-3 py-2">
											Marks
										</th>
										<th scope="col" className="px-3 py-2">
											Language
										</th>
										<th scope="col" className="px-3 py-2">
											Core match
										</th>
									</tr>
								</thead>
								<tbody>
									{validRows.map((row) => (
										<tr key={row.rowIndex} className="transition-colors hover:bg-slate-50">
											<td className="px-4 py-3 text-slate-700">{row.rowIndex}</td>
											<td className="px-4 py-3 text-slate-700">{row.data.questionNumber}</td>
											<td className="px-4 py-3 text-slate-700">{row.data.maxMarks}</td>
											<td className="px-4 py-3 text-slate-700">{row.data.language}</td>
											<td className="px-4 py-3 text-slate-700">
												{row.warnings.length === 0 ? (
													<span className="text-green-700">✓</span>
												) : (
													<span className="text-amber-700">{row.warnings.map((w) => w.message).join("; ")}</span>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
					{invalidRows.length === 0 && (
						<div className="flex flex-row gap-4 mb-2 justify-center">
							<button
								type="button"
								onClick={() => navigate(-1)}
								className="bg-gray-200 text-gray-700 px-6 h-8 rounded-md font-semibold cursor-pointer"
							>
								Cancel
							</button>

							<button
								type="submit"
								onClick={handleDryRun}
								disabled={isLoading}
								className="bg-(--admin-color-primary-strong) cursor-pointer text-white px-6 h-8 rounded-md font-semibold"
							>
								{isLoading ? "Loading..." : "Dry-run"}
							</button>
						</div>
					)}
				</>
			)}
		</div>
	);
};
